/**
 * ContractEditModal — bearbeitet Domain-Felder eines Vertrags (Typ, Lieferant, Betrag, Laufzeit).
 *
 * Use-Case: jemand hat den Vertrag beim Upload halbgar erfasst (z.B. "Strom" ohne
 * Betrag), spaeter sollen die Felder ohne neuen Upload nachgepflegt werden.
 * Backend: PUT /api/v1/properties/{propertyId}/contracts/{id} mit
 * UpdateContractRequest — alle Felder sind optional (partial update).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@/shared/components';
import {
  toApiError,
  useApi,
  type ContractResponse,
  type UpdateContractRequest,
} from '@/shared/api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  contract: ContractResponse | null;
}

export function ContractEditModal({ open, onOpenChange, propertyId, contract }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const [type, setType] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [noticePeriodDays, setNoticePeriodDays] = useState('');
  const [autoRenewal, setAutoRenewal] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && contract) {
      setType(contract.type);
      setCounterparty(contract.counterparty);
      setMonthlyAmount(contract.monthlyAmount != null ? String(contract.monthlyAmount) : '');
      setCurrency(contract.currency);
      setStartDate(contract.startDate);
      setEndDate(contract.endDate ?? '');
      setNoticePeriodDays(
        contract.noticePeriodDays != null ? String(contract.noticePeriodDays) : '',
      );
      setAutoRenewal(contract.autoRenewal);
      setNotes(contract.notes ?? '');
    }
  }, [open, contract]);

  const update = useMutation({
    mutationFn: (body: UpdateContractRequest) => {
      if (!contract) return Promise.reject(new Error('Kein Vertrag ausgewählt'));
      return api.contracts.update(propertyId, contract.id, body);
    },
    onSuccess: () => {
      toast.success('Vertrag aktualisiert');
      void qc.invalidateQueries({ queryKey: ['contracts', propertyId] });
      void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
      onOpenChange(false);
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  function submit() {
    if (!contract) return;
    if (!type.trim() || !counterparty.trim() || !startDate) {
      toast.error('Typ, Lieferant und Startdatum sind Pflichtfelder.');
      return;
    }
    update.mutate({
      type: type.trim(),
      counterparty: counterparty.trim(),
      monthlyAmount: monthlyAmount.trim() ? Number(monthlyAmount.replace(',', '.')) : null,
      currency: currency || 'EUR',
      startDate,
      endDate: endDate || null,
      noticePeriodDays: noticePeriodDays ? Number(noticePeriodDays) : null,
      autoRenewal,
      notes: notes.trim() || null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vertrag bearbeiten</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Typ" required>
              <Input value={type} onChange={(e) => setType(e.target.value)} placeholder="Strom" />
            </FormField>
            <FormField label="Lieferant" required>
              <Input
                value={counterparty}
                onChange={(e) => setCounterparty(e.target.value)}
                placeholder="Vodafone"
              />
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Betrag/Monat" className="col-span-2">
              <Input
                type="number"
                step="0.01"
                value={monthlyAmount}
                onChange={(e) => setMonthlyAmount(e.target.value)}
                placeholder="29,99"
              />
            </FormField>
            <FormField label="Währung">
              <Input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Start" required>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </FormField>
            <FormField label="Ende">
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 items-end gap-2">
            <FormField label="Kündigungsfrist (Tage)">
              <Input
                type="number"
                value={noticePeriodDays}
                onChange={(e) => setNoticePeriodDays(e.target.value)}
                placeholder="z.B. 30"
              />
            </FormField>
            <label className="text-ink flex items-center gap-2 pb-3 text-sm">
              <input
                type="checkbox"
                checked={autoRenewal}
                onChange={(e) => setAutoRenewal(e.target.checked)}
                className="border-ink-faint h-4 w-4 rounded"
              />
              <span>Automatische Verlängerung</span>
            </label>
          </div>
          <FormField label="Notizen">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button variant="brand" onClick={submit} loading={update.isPending} disabled={!contract}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
